import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useEffect, useRef } from 'react';
import { STOP_CLUSTERING_ZOOM } from '../constants';
import { createGeoEventMarkers, type GeoEventMarkerData } from '../utils/mapOverlays';

interface UseEventMarkersClusteringArgs {
  map: google.maps.Map | null;
  mapLoaded: boolean;
  enabled: boolean;
  events: ReadonlyArray<GeoEventMarkerData>;
}

export function useEventMarkersClustering({
  map,
  mapLoaded,
  enabled,
  events,
}: UseEventMarkersClusteringArgs): void {
  const markersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  useEffect(() => {
    if (!map || !mapLoaded || !window.google?.maps) return;

    for (const marker of markersRef.current) {
      marker.setMap(null);
    }
    markersRef.current = [];

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }

    if (!enabled || events.length === 0) return;

    const markers = createGeoEventMarkers(map, events);
    markersRef.current = markers;

    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      algorithmOptions: { maxZoom: STOP_CLUSTERING_ZOOM },
    });

    return () => {
      for (const marker of markersRef.current) {
        marker.setMap(null);
      }
      markersRef.current = [];
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }
    };
  }, [map, mapLoaded, enabled, events]);
}
